import { obj } from '@easeltv/utilities';
import { useMemo, useRef } from 'react';

import { useApplicationContext } from '../../providers/ApplicationProvider/ApplicationProvider';

/**
 * useDictionary
 *
 * Custom hook to give quick access to the dictionary object
 *
 * @returns
 */
export function useDictionary(): Record<string, string>;

export function useDictionary(id: string): string;

export function useDictionary(id?: string) {
    const { dictionary } = useApplicationContext();

    if (id) {
        return dictionary.definitions[id] || '';
    }

    return dictionary.definitions;
}

/**
 * useDictionaryObject
 *
 * Custom hook returns values from the dictionary in the same shape as the object passed to it
 *
 * @param dictionaryObject - Object with the dictionary key passed as the value
 * @returns An object with the dictionary keys replaced with their respective values from the dictionary
 */
export function useDictionaryObject<T extends string>(dictionaryObject: {
    [K in T]: string;
}): { [K in T]: string } {
    // Stored in a ref due to object inequality between causing re-renders
    const dictObj = useRef(dictionaryObject);

    const { dictionary } = useApplicationContext();

    // Wrap in useMemo to prevent re-renders
    return useMemo(() => {
        // Create a map from the given object
        const dictMap = new Map<T, string>(obj.entries(dictObj.current));

        // Manipulate the map to replace the value of each entry with the value from the dictionary
        dictMap.forEach((value, key, map) => {
            map.set(key, dictionary.definitions[value] || '');
        });

        // Return an object of the same shape as the argument containing the dictionary strings
        return obj.fromEntries<{ [K in T]: string }>(Array.from(dictMap));
    }, [dictionary.definitions]);
}
